import { useEffect, useState } from 'react'

const ViewJobModal = ({ isOpen, onClose, job }) => {
  if (!isOpen || !job) return null

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      pending: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700',
      completed: 'bg-gray-100 text-gray-700'
    }
    
    const icons = {
      active: 'fa-play-circle',
      paused: 'fa-pause-circle',
      pending: 'fa-clock',
      failed: 'fa-exclamation-circle',
      completed: 'fa-check-circle'
    }
    
    return (
      <span className={`px-3 py-1 ${badges[status] || badges.pending} text-xs font-bold rounded-full flex items-center w-fit`}>
        <i className={`fa-solid ${icons[status]} mr-1 text-[8px]`}></i>
        {status.toUpperCase()}
      </span>
    )
  }

  const getFrequencyBadge = (frequency) => {
    const badges = {
      hourly: 'bg-orange-100 text-orange-700',
      daily: 'bg-blue-100 text-blue-700',
      weekly: 'bg-purple-100 text-purple-700',
      monthly: 'bg-indigo-100 text-indigo-700',
      custom: 'bg-teal-100 text-teal-700'
    }
    
    const icons = {
      hourly: 'fa-clock',
      daily: 'fa-calendar-day',
      weekly: 'fa-calendar-week',
      monthly: 'fa-calendar-alt',
      custom: 'fa-arrows-rotate'
    }
    
    return (
      <span className={`px-3 py-1 ${badges[frequency] || badges.custom} text-xs font-bold rounded-full`}>
        <i className={`fa-solid ${icons[frequency]} mr-1`}></i>
        {frequency.toUpperCase()}
      </span>
    )
  }

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-8">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-light-200/20 px-8 py-6 flex items-center justify-between z-10">
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-bold text-dark-300">Job Details</h2>
            {getStatusBadge(job.status)}
            {getFrequencyBadge(job.schedule?.frequency || 'custom')}
          </div>
          <button 
            onClick={() => onClose()}
            className="p-2 hover:bg-light-50 rounded-lg transition-colors"
          >
            <i className="fa-solid fa-times text-dark-100 text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          {/* Basic Info */}
          <section>
            <h3 className="text-lg font-bold text-dark-300 mb-4">Basic Information</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="w-32 text-sm font-semibold text-dark-100">Name:</div>
                <div className="flex-1 text-sm text-dark-300">{job.name}</div>
              </div>
              {job.description && (
                <div className="flex items-start">
                  <div className="w-32 text-sm font-semibold text-dark-100">Description:</div>
                  <div className="flex-1 text-sm text-dark-300">{job.description}</div>
                </div>
              )}
              <div className="flex items-start">
                <div className="w-32 text-sm font-semibold text-dark-100">Created:</div>
                <div className="flex-1 text-sm text-dark-300">{new Date(job.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-start">
                <div className="w-32 text-sm font-semibold text-dark-100">Last Updated:</div>
                <div className="flex-1 text-sm text-dark-300">{new Date(job.updated_at).toLocaleString()}</div>
              </div>
            </div>
          </section>

          {/* Schedule Info */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Schedule Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Frequency</div>
                <div className="text-sm font-semibold text-dark-300 capitalize">{job.schedule?.frequency || 'N/A'}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Time (UTC)</div>
                <div className="text-sm font-semibold text-dark-300">{job.schedule?.time || 'N/A'}</div>
              </div>
              {job.schedule?.days_of_week && job.schedule.days_of_week.length > 0 && (
                <div className="bg-white rounded-lg p-4 col-span-2">
                  <div className="text-xs text-dark-100 mb-1">Days of Week</div>
                  <div className="text-sm font-semibold text-dark-300">
                    {job.schedule.days_of_week.map(d => daysOfWeek[d]).join(', ')}
                  </div>
                </div>
              )}
              {job.schedule?.day_of_month && (
                <div className="bg-white rounded-lg p-4">
                  <div className="text-xs text-dark-100 mb-1">Day of Month</div>
                  <div className="text-sm font-semibold text-dark-300">{job.schedule.day_of_month}</div>
                </div>
              )}
              {job.schedule?.interval_hours && (
                <div className="bg-white rounded-lg p-4">
                  <div className="text-xs text-dark-100 mb-1">Interval Hours</div>
                  <div className="text-sm font-semibold text-dark-300">{job.schedule.interval_hours}</div>
                </div>
              )}
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Next Run</div>
                <div className="text-sm font-semibold text-primary-500">
                  {job.next_run ? new Date(job.next_run).toLocaleString() : 'N/A'}
                </div>
              </div>
              {job.last_run && (
                <div className="bg-white rounded-lg p-4">
                  <div className="text-xs text-dark-100 mb-1">Last Run</div>
                  <div className="text-sm font-semibold text-dark-300">{new Date(job.last_run).toLocaleString()}</div>
                </div>
              )}
            </div>
          </section>

          {/* Extraction Config */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Extraction Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Mode</div>
                <div className="text-sm font-semibold text-dark-300 capitalize">{job.mode}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Template ID</div>
                <div className="text-sm font-semibold text-dark-300">{job.template_id || 'None'}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Max Results</div>
                <div className="text-sm font-semibold text-dark-300">{job.max_results}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">LLM Model</div>
                <div className="text-sm font-semibold text-dark-300">{job.llm_model || 'N/A'}</div>
              </div>
              {job.search_query && (
                <div className="bg-white rounded-lg p-4 col-span-2">
                  <div className="text-xs text-dark-100 mb-1">Search Query</div>
                  <div className="text-sm font-semibold text-dark-300">{job.search_query}</div>
                </div>
              )}
            </div>
          </section>

          {/* Statistics */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Statistics</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Total Runs</div>
                <div className="text-2xl font-bold text-dark-300">{job.total_runs}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Successful</div>
                <div className="text-2xl font-bold text-green-600">{job.successful_runs}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Failed</div>
                <div className="text-2xl font-bold text-red-600">{job.failed_runs}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-xs text-dark-100 mb-1">Success Rate</div>
                <div className="text-2xl font-bold text-primary-500">{job.success_rate?.toFixed(1) || '0.0'}%</div>
              </div>
            </div>
          </section>

          {/* Notifications */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Notifications</h3>
            <div className="space-y-2">
              <div className="flex items-center">
                <i className={`fa-solid fa-${job.notifications?.email_on_success ? 'check-circle text-green-600' : 'times-circle text-gray-400'} mr-2`}></i>
                <span className="text-sm text-dark-300">Email on Success</span>
              </div>
              <div className="flex items-center">
                <i className={`fa-solid fa-${job.notifications?.email_on_failure ? 'check-circle text-green-600' : 'times-circle text-gray-400'} mr-2`}></i>
                <span className="text-sm text-dark-300">Email on Failure</span>
              </div>
              {job.notifications?.webhook_url && (
                <div className="flex items-center">
                  <i className="fa-solid fa-check-circle text-green-600 mr-2"></i>
                  <span className="text-sm text-dark-300">Webhook: {job.notifications.webhook_url}</span>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-light-200/20 px-8 py-6 flex items-center justify-end">
          <button 
            onClick={() => onClose()}
            className="px-6 py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-semibold transition-all shadow-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewJobModal
